import { useState, useEffect, useCallback, useRef } from 'react'

function safeJSONParse<T>(value: string | null, fallback: T): T {
  if (!value) return fallback
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

/**
 * A custom React hook that provides reactive access to sessionStorage values.
 * Automatically syncs state across browser tabs and handles JSON serialization.
 *
 * @param key - The sessionStorage key to store/retrieve the value
 * @param initialValue - Default value if no value exists in sessionStorage
 * @returns [value, setValue, clearValue, isMounted] - Current value, function to update it, function to clear it, and a flag indicating if the component is mounted
 * @template T - Type of the stored value
 */
export function useLocalStorage<T>(key: string, initialValue: T) {
  const mountedRef = useRef(false)
  const [isMounted, setIsMounted] = useState(false)

  // Get initial value from localStorage or use initialValue
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initialValue
    return safeJSONParse(localStorage.getItem(key), initialValue)
  })

  // Re-read from localStorage when key changes
  // This is essential for hooks that change their key dynamically (e.g., switching between chats)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const storedValue = safeJSONParse(localStorage.getItem(key), initialValue)
    setValue(storedValue)
  }, [key, initialValue])

  // Listen to storage events from other tabs/windows
  useEffect(() => {
    mountedRef.current = true
    setIsMounted(true)

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key && event.storageArea === localStorage) {
        setValue(event.newValue ? JSON.parse(event.newValue) : initialValue)
      }
    }

    // Add event listener
    window.addEventListener('storage', handleStorageChange)

    // Cleanup
    return () => {
      mountedRef.current = false
      setIsMounted(false)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [key, initialValue])

  // Function to update storage
  const updateValue = useCallback(
    (newValue: T) => {
      if (!mountedRef.current) return

      setValue(newValue)
      if (typeof window !== 'undefined') {
        localStorage.setItem(key, JSON.stringify(newValue))
        // Dispatch custom event to notify other components
        window.dispatchEvent(
          new StorageEvent('storage', {
            key,
            newValue: JSON.stringify(newValue),
            storageArea: localStorage,
          })
        )
      }
    },
    [key]
  )

  const clearValue = useCallback(() => {
    if (!mountedRef.current) return

    setValue(initialValue)
    if (typeof window !== 'undefined') {
      localStorage.removeItem(key)
      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          newValue: null,
          storageArea: localStorage,
        })
      )
    }
  }, [key, initialValue])

  return [value, updateValue, clearValue, isMounted] as const
}
